import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { useGetDashboardKpiQuery } from '../order/orders-api-slice';
import { selectCurrentUser } from '../user';
import {
  ManufacturerFilter,
  MondayWeekStartField,
  PieStageTooltip,
  ReportLayout,
  ReportTablePagination,
  SortableHeadCell,
  useSortableTable,
} from './components';
import { getMostRecentMonday } from './report-utils';

interface StageRow {
  name: string;
  value: number;
  percentage: string;
  color: string;
  // Index signature required so the rows satisfy Recharts' Pie `data` prop type.
  [key: string]: string | number;
}

const STAGE_ORDER: Record<string, number> = { Saw: 0, Weld: 1, Paint: 2 };

const STAGE_SORT_ACCESSORS: Record<string, (row: StageRow) => string | number> = {
  name: r => STAGE_ORDER[r.name] ?? 99,
  value: r => r.value,
  percentage: r => parseFloat(r.percentage),
};

/**
 * UDOMI-506: Total Feet Per Week Report
 * Displays a pie chart showing total weekly production breakdown (Saw, Weld, Paint) with percentages.
 * Accessible to: Administrator, Supervisor, CSR
 * Administrators can select a manufacturer; others see their assigned manufacturer.
 */
export default function TotalFeetPerWeekReport() {
  const currentUser = useAppSelector(selectCurrentUser);

  // Production stage colors per AC specification (Saw=yellow, Weld=orange, Paint=purple)
  const sawColor = '#ffcc00';
  const weldColor = '#ff6633';
  const paintColor = '#a600ff';

  // Determine user role
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;

  // State: table vs graph view toggle
  const [viewMode, setViewMode] = useState<'table' | 'graph'>('table');

  // State: selected Monday date (YYYY-MM-DD format)
  const [selectedMonday, setSelectedMonday] = useState<string>(
    getMostRecentMonday().toISODate() ?? ''
  );

  // State: selected manufacturer (admin only)
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    undefined
  );

  // Calculate start/end dates for the week
  const startDate = selectedMonday; // Monday
  const endDate = selectedMonday
    ? DateTime.fromISO(selectedMonday).plus({ days: 4 }).toISODate()
    : '';

  // Build request for Dashboard KPI — admin: undefined until manufacturer selected (query skipped);
  // non-admin: auto-scoped to their assigned manufacturer (BR-1).
  const kpiRequest = useMemo(() => {
    const manufacturerId = isAdmin ? selectedManufacturerId : currentUser?.manufacturerId;

    return {
      manufacturerId,
      viewMode: 'week' as const,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      includePartialPeriods: true,
    };
  }, [isAdmin, selectedManufacturerId, currentUser?.manufacturerId, startDate, endDate]);

  // Fetch Dashboard KPI data — skip until manufacturerId is resolved (admin must select one; BR-1)
  const {
    data: kpiData,
    isLoading,
    error,
  } = useGetDashboardKpiQuery(kpiRequest, {
    skip: !kpiRequest.manufacturerId,
  });

  // Transform data for pie chart
  const pieData = useMemo<StageRow[]>(() => {
    if (!kpiData?.productionMetricsByStage) {
      return [];
    }

    const { sawFeet, weldFeet, paintFeet } = kpiData.productionMetricsByStage;
    const total = sawFeet + weldFeet + paintFeet;

    if (total === 0) {
      return [];
    }

    return [
      {
        name: 'Saw',
        value: sawFeet,
        percentage: ((sawFeet / total) * 100).toFixed(1),
        color: sawColor,
      },
      {
        name: 'Weld',
        value: weldFeet,
        percentage: ((weldFeet / total) * 100).toFixed(1),
        color: weldColor,
      },
      {
        name: 'Paint',
        value: paintFeet,
        percentage: ((paintFeet / total) * 100).toFixed(1),
        color: paintColor,
      },
      // UDOMI-896: Exclude 0-value stages to prevent empty labels overlapping the chart
    ].filter(item => item.value > 0);
  }, [kpiData, sawColor, weldColor, paintColor]);

  // Custom label renderer for pie chart
  const renderLabel = useCallback(
    (props: { name?: string; percentage?: string }) => {
      const entry = pieData.find(d => d.name === props.name);
      return entry ? `${entry.name}: ${entry.percentage}%` : '';
    },
    [pieData]
  );

  const {
    pageRows,
    sortedRows,
    page,
    rowsPerPage,
    isLastPage,
    getSortProps,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSortableTable<StageRow>({
    data: pieData,
    accessors: STAGE_SORT_ACCESSORS,
    initialSortField: 'name',
    initialSortDirection: 'asc',
  });

  return (
    <ReportLayout
      title="Total Feet Per Week Report"
      description="Weekly production totals by stage with percentage breakdown"
      isLoading={isLoading}
      error={error}
      filters={
        <>
          <ManufacturerFilter
            isAdmin={isAdmin}
            selectedManufacturerId={selectedManufacturerId}
            onManufacturerChange={setSelectedManufacturerId}
          />
          <MondayWeekStartField value={selectedMonday} onChange={setSelectedMonday} />
        </>
      }
    >
      {isAdmin && !selectedManufacturerId ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="body1" color="text.secondary">
            Please select a manufacturer to view this report.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Table / Graph toggle */}
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => {
                if (v) setViewMode(v);
              }}
              size="small"
            >
              <ToggleButton value="table">Table</ToggleButton>
              <ToggleButton value="graph">Graph</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Card — Table or Chart */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total for the Week of {DateTime.fromISO(selectedMonday).toFormat('MMMM d, yyyy')}
              </Typography>
              {pieData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <Typography variant="body1" color="text.secondary">
                    No production data available for this week
                  </Typography>
                </Box>
              ) : viewMode === 'table' ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <SortableHeadCell sx={{ fontWeight: 'bold' }} {...getSortProps('name')}>
                          Stage
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold' }}
                          align="right"
                          {...getSortProps('value')}
                        >
                          Feet (ft)
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold' }}
                          align="right"
                          {...getSortProps('percentage')}
                        >
                          %
                        </SortableHeadCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map(row => (
                        <TableRow key={row.name} hover>
                          <TableCell sx={{ color: row.color, fontWeight: 600 }}>
                            {row.name}
                          </TableCell>
                          <TableCell align="right">{row.value.toFixed(2)}</TableCell>
                          <TableCell align="right">{row.percentage}%</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row (shown on the last page) */}
                      {isLastPage && (
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {sortedRows.reduce((s, r) => s + r.value, 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            100%
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <ReportTablePagination
                    count={pieData.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={renderLabel}
                      labelLine
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieStageTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Summary Card */}
          {kpiData?.productionMetricsByStage && pieData.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Week Summary
                </Typography>
                <Box display="flex" gap={4} flexWrap="wrap">
                  {pieData.map(item => (
                    <Box key={item.name}>
                      <Typography variant="body2" color="text.secondary">
                        {item.name}
                      </Typography>
                      <Typography variant="h5" sx={{ color: item.color }}>
                        {item.value.toFixed(2)} ft
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.percentage}%
                      </Typography>
                    </Box>
                  ))}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h5">
                      {pieData.reduce((sum, item) => sum + item.value, 0).toFixed(2)} ft
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </ReportLayout>
  );
}
